/* One controller for all Okendo full review widgets, including app blocks and dialogs. */
(() => {

function mount(root) {
  if (!root || !window.ResizeObserver || !window.MutationObserver || !window.IntersectionObserver) return;
  if (root.pfsReviewCleanup) return;
  root.classList.add('pfs-shared-review-widget');
  let list, cards = [], frame = 0, stopped = false, near = false;
  let lastCount = 0, lastSignature = '', toppedUp = false;
  const set = (element, name, value) => {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  };
  const restore = () => {
    if (!list) return;
    list.removeAttribute('data-pfs-masonry');
    list.style.removeProperty('--pfs-review-height');
    cards.forEach(card => {
      card.style.removeProperty('--pfs-review-x');
      card.style.removeProperty('--pfs-review-y');
    });
  };
  const schedule = () => {
    if (!stopped && !frame) frame = requestAnimationFrame(update);
  };
  let dialog = root.closest('#oke-modalContainer, .pfs_header_reviews-modal-wrap');
  let pending = false, requestedCount = 0, armed = true, failed = false, timer = 0;
  let watchedButton = null;
  const status = document.createElement('div');
  status.className = 'pfs-review-loading';
  status.setAttribute('role', 'status');
  status.hidden = true;
  status.textContent = 'Loading more reviews…';
  const finishLoad = () => {
    pending = false;
    clearTimeout(timer);
    status.hidden = true;
    root.classList.remove('pfs-reviews-loading');
  };
  const scrolling = () => { if (!pending) armed = true; schedule(); };
  const bottomObserver = new IntersectionObserver(schedule);
  if (dialog) dialog.addEventListener('scroll', scrolling, {capture: true, passive: true});
  function paginate(button, count, reset) {
    const currentDialog = root.closest('#oke-modalContainer, .pfs_header_reviews-modal-wrap');
    if (currentDialog !== dialog) {
      dialog?.removeEventListener('scroll', scrolling, true);
      dialog = currentDialog;
      dialog?.addEventListener('scroll', scrolling, {capture: true, passive: true});
      finishLoad();
      armed = true;
      failed = false;
    }
    if (!dialog) return;
    if (reset) { finishLoad(); failed = false; armed = true; }
    if (pending && (count > requestedCount || !button)) finishLoad();
    if (button !== watchedButton) {
      bottomObserver.disconnect();
      watchedButton = button;
      if (button) bottomObserver.observe(button);
    }
    if (!button || pending || failed || !armed || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    for (let node = button; node; node = node.parentElement) {
      const css = getComputedStyle(node);
      if (node.hidden || css.display === 'none' || css.visibility === 'hidden') return;
    }
    let clipTop = 0, clipBottom = innerHeight;
    for (let node = button.parentElement; node; node = node.parentElement) {
      const css = getComputedStyle(node);
      if (/auto|scroll|hidden/.test(css.overflowY)) {
        const rect = node.getBoundingClientRect();
        clipTop = Math.max(clipTop, rect.top);
        clipBottom = Math.min(clipBottom, rect.bottom);
      }
      if (css.position === 'fixed') break;
    }
    const rect = button.getBoundingClientRect();
    if (!rect.height || clipBottom <= clipTop || rect.top > clipBottom || rect.bottom < clipTop) return;
    armed = false;
    pending = true;
    requestedCount = count;
    if (!status.isConnected) root.append(status);
    status.hidden = false;
    root.classList.add('pfs-reviews-loading');
    timer = setTimeout(() => { finishLoad(); failed = true; }, 15000);
    button.click();
  }
  const resize = new ResizeObserver(schedule);
  const visible = new IntersectionObserver(entries => {
    near = entries.some(entry => entry.isIntersecting);
    if (near) schedule();
  }, { rootMargin: '300px 0px' });
  const mutations = new MutationObserver(schedule);
  function cleanup() {
    stopped = true;
    cancelAnimationFrame(frame);
    resize.disconnect();
    visible.disconnect();
    mutations.disconnect();
    bottomObserver.disconnect();
    finishLoad();
    status.remove();
    dialog?.removeEventListener('scroll', scrolling, true);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('pagehide', leave);
    root.removeEventListener('load', schedule, true);
    root.removeEventListener('click', manualLoad, true);
    document.removeEventListener('shopify:section:unload', unload);
    restore();
    delete root.pfsReviewCleanup;
  }
  function leave(event) { if (!event.persisted) cleanup(); }
  function unload(event) { if (event.target.contains(root)) cleanup(); }
  function manualLoad(event) {
    if (event.isTrusted && event.target.closest('.oke-showMore-button')) { toppedUp = true; failed = false; armed = false; }
  }
  function update() {
    frame = 0;
    if (stopped) return;
    if (!root.isConnected) { cleanup(); return; }
    try {
      const nextList = root.querySelector('.okeReviews.oke-w .oke-w-reviews-list');
      if (nextList !== list) {
        restore();
        resize.disconnect();
        visible.disconnect();
        list = nextList;
        cards = [];
        near = false;
        lastCount = 0;
        lastSignature = '';
        toppedUp = false;
        if (list) {
          resize.observe(list);
          visible.observe(list);
        }
      }
      if (!list) return;
      const nextCards = Array.from(list.children).filter(card => card.classList.contains('oke-w-reviews-list-item'));
      cards.filter(card => !nextCards.includes(card)).forEach(card => resize.unobserve(card));
      nextCards.filter(card => !cards.includes(card)).forEach(card => resize.observe(card));
      cards = nextCards;
      const signature = cards.slice(0, 5).map(card =>
        (card.querySelector('.oke-w-reviewer-name')?.textContent || '') + '\n' +
        (card.querySelector('.oke-reviewContent-title')?.textContent || '')
      ).join('\n\n');
      if (cards.length <= 5 && (cards.length < lastCount || signature !== lastSignature)) toppedUp = false;
      const paginationReset = cards.length < lastCount || (lastSignature && signature !== lastSignature);
      lastCount = cards.length;
      lastSignature = signature;
      if (!cards.length) { restore(); return; }
      if (list.getBoundingClientRect().width >= 600) {
        list.setAttribute('data-pfs-masonry', '');
        const style = getComputedStyle(list);
        const columns = list.getBoundingClientRect().width >= 900 ? 3 : 2;
        set(list, '--pfs-review-columns', String(columns));
        const gap = Number(style.getPropertyValue('--pfs-review-gap'));
        const width = list.getBoundingClientRect().width;
        if (!width || !columns) { restore(); return; }
        const cardWidth = (width - gap * (columns - 1)) / columns;
        const heights = Array(columns).fill(0);
        const sizes = cards.map(card => card.getBoundingClientRect().height);
        cards.forEach((card, index) => {
          const column = heights.indexOf(Math.min(...heights));
          set(card, '--pfs-review-x', `${column * (cardWidth + gap)}px`);
          set(card, '--pfs-review-y', `${heights[column]}px`);
          heights[column] += sizes[index] + gap;
        });
        set(list, '--pfs-review-height', `${Math.max(...heights) - gap}px`);
      } else restore();
      const button = root.querySelector('.oke-showMore-button');
      paginate(button, cards.length, paginationReset);
      if (!dialog && near && !toppedUp && cards.length === 5 && button && !button.disabled &&
          button.getAttribute('aria-disabled') !== 'true' && button.getBoundingClientRect().height > 0) {
        toppedUp = true;
        button.click();
      }
    } catch (error) {
      cleanup();
      console.warn('Review layout kept its standard grid.', error);
    }
  }
  root.pfsReviewCleanup = cleanup;
  mutations.observe(root, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pagehide', leave);
  root.addEventListener('load', schedule, true);
  root.addEventListener('click', manualLoad, true);
  document.addEventListener('shopify:section:unload', unload);
  if (document.fonts) document.fonts.ready.then(schedule);
  schedule();
}
  let scanFrame = 0;
  const mounted = new Set();
  function scan() {
    scanFrame = 0;
    mounted.forEach(root => { if (!root.isConnected) { root.pfsReviewCleanup?.(); mounted.delete(root); } });
    document.querySelectorAll('.okeReviews.oke-w').forEach(root => {
      if (!mounted.has(root)) { mount(root); mounted.add(root); }
    });
  }
  const watcher = new MutationObserver(() => { if (!scanFrame) scanFrame = requestAnimationFrame(scan); });
  watcher.observe(document.body, {childList:true, subtree:true});
  document.addEventListener('shopify:section:load', scan);
  scan();
})();
